// Configuração de personalização por UTM
// Permite mostrar conteúdo diferente baseado na origem do tráfego

export interface UTMPersonalization {
  headline?: string
  subtitle?: string
  cta?: string
  emphasis?: 'price' | 'quality' | 'trust' | 'speed'
  showPromo?: boolean
  preselect?: {
    objective?: 'leads' | 'sales' | 'portfolio' | 'branding' | 'info'
    projectType?: 'simples' | 'institucional' | 'personalizado'
  }
}

export const UTM_PERSONALIZATIONS = {
  // Personalização por source (utm_source)
  sources: {
    google: {
      headline: 'Site Profissional para Seu Negócio',
      subtitle: 'Encontrou o que procurava!',
      cta: 'Solicitar Orçamento',
      emphasis: 'trust' as const
    },
    facebook: {
      headline: 'Transforme Sua Presença Digital',
      subtitle: 'Viu nosso anúncio? Vamos conversar!',
      cta: 'Quero Saber Mais',
      emphasis: 'quality' as const
    },
    instagram: {
      headline: 'Design Premium para Sua Marca',
      subtitle: 'Visual que converte seguidores em clientes',
      cta: 'Criar Meu Site',
      emphasis: 'quality' as const
    },
    tiktok: {
      headline: 'Seu Negócio no Próximo Nível',
      subtitle: 'Site moderno que impressiona',
      cta: 'Começar Agora',
      emphasis: 'speed' as const
    },
    linkedin: {
      headline: 'Presença Digital Corporativa',
      subtitle: 'Site que transmite profissionalismo',
      cta: 'Solicitar Proposta',
      emphasis: 'trust' as const
    }
  } as Record<string, UTMPersonalization>,

  // Personalização por campanha (utm_campaign)
  campaigns: {
    ecommerce: {
      headline: 'Loja Virtual que Vende de Verdade',
      subtitle: 'E-commerce otimizado para conversão',
      preselect: { objective: 'sales' as const }
    },
    institucional: {
      headline: 'Site Institucional Profissional',
      subtitle: 'Credibilidade para sua empresa',
      preselect: { objective: 'portfolio' as const }
    },
    landing: {
      headline: 'Landing Page de Alta Conversão',
      subtitle: 'Capture mais leads qualificados',
      preselect: { objective: 'leads' as const }
    },
    branding: {
      headline: 'Identidade Visual Completa',
      subtitle: 'Marca forte, negócio forte',
      preselect: { objective: 'branding' as const }
    },
    promo: {
      headline: 'Oferta Especial por Tempo Limitado',
      subtitle: 'Aproveite condições exclusivas',
      showPromo: true,
      emphasis: 'price' as const
    },
    remarketing: {
      headline: 'Ainda Pensando no Seu Site?',
      subtitle: 'Vamos tirar suas dúvidas!',
      cta: 'Conversar Agora'
    }
  } as Record<string, UTMPersonalization>,

  // Personalização por keyword (utm_term)
  terms: {
    'criar site': { emphasis: 'price' as const },
    'fazer site': { emphasis: 'price' as const },
    'site barato': { emphasis: 'price' as const, showPromo: true },
    'site profissional': { emphasis: 'quality' as const },
    'agencia site': { emphasis: 'trust' as const },
    'site empresa': { emphasis: 'trust' as const },
    'loja virtual': { preselect: { objective: 'sales' as const } },
    'ecommerce': { preselect: { objective: 'sales' as const } },
    'landing page': { preselect: { objective: 'leads' as const } },
    'site institucional': { preselect: { objective: 'portfolio' as const } }
  } as Record<string, UTMPersonalization>
}

// Mensagens de ênfase por tipo
export const EMPHASIS_MESSAGES = {
  price: {
    badge: 'Melhor Custo-Benefício',
    message: 'Investimento que cabe no seu bolso'
  },
  quality: {
    badge: 'Design Premium',
    message: 'Qualidade que diferencia sua marca'
  },
  trust: {
    badge: '+500 Projetos Entregues',
    message: 'Experiência que você pode confiar'
  },
  speed: {
    badge: 'Entrega Rápida',
    message: 'Seu site pronto em tempo recorde'
  }
}

// Função para obter personalização baseada nos UTMs
export function getPersonalization(utm: {
  source?: string | null
  campaign?: string | null
  term?: string | null
}): UTMPersonalization {
  let result: UTMPersonalization = {}

  // 1. Aplicar personalização por source (base)
  if (utm.source) {
    const sourceKey = utm.source.toLowerCase()
    if (UTM_PERSONALIZATIONS.sources[sourceKey]) {
      result = { ...result, ...UTM_PERSONALIZATIONS.sources[sourceKey] }
    }
  }

  // 2. Aplicar personalização por campaign (sobrescreve)
  if (utm.campaign) {
    const campaignKey = utm.campaign.toLowerCase()
    // Busca parcial (ex: "ecommerce_promo_jan" contém "ecommerce")
    for (const [key, value] of Object.entries(UTM_PERSONALIZATIONS.campaigns)) {
      if (campaignKey.includes(key)) {
        result = { ...result, ...value }
        break
      }
    }
  }

  // 3. Aplicar personalização por term (sobrescreve)
  if (utm.term) {
    const termKey = utm.term.toLowerCase()
    for (const [key, value] of Object.entries(UTM_PERSONALIZATIONS.terms)) {
      if (termKey.includes(key)) {
        result = { ...result, ...value }
        break
      }
    }
  }

  return result
}
